import { Component, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OperationalChargesService } from '../../../../services/forwarding/operational-charges';
import { OperationalCharge, ChargeMode } from '../../../../models/forwarding/operational-charge.model';
import { ChargeBasisService } from '../../../../services/forwarding/charge-basis.service';
import { ChargeBasis } from '../../../../models/forwarding/charge-basis.model';

const CHARGE_CODE = 'OT';

@Component({
  selector: 'app-other-charges-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './other-charges-modal.html',
  styleUrl: './other-charges-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtherChargesModalComponent implements OnInit {
  private readonly operationalChargesService = inject(OperationalChargesService);
  private readonly chargeBasisService = inject(ChargeBasisService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  isOpen = false;
  showForm = false;
  isEditMode = false;
  editingChargeId: number | null = null;

  allCharges: OperationalCharge[] = [];
  filteredCharges: OperationalCharge[] = [];
  chargeBasisList: ChargeBasis[] = [];

  searchTerm = '';
  selectedMode = '';
  selectedChargeType = '';

  formData: Partial<OperationalCharge> = this.getEmptyFormData();

  get stats() {
    const total = this.filteredCharges.length;
    const mandatory = this.filteredCharges.filter(c => !c.isOptional).length;
    const totalPrice = this.filteredCharges.reduce((sum, c) => sum + c.defaultPrice, 0);
    return {
      totalCharges: total,
      mandatoryCharges: mandatory,
      optionalCharges: total - mandatory,
      averagePrice: total > 0 ? totalPrice / total : 0
    };
  }

  ngOnInit(): void {
    this.operationalChargesService.getCharges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(charges => {
        this.allCharges = charges.filter(c => c.chargeCode === CHARGE_CODE);
        this.applyFilters();
        this.cdr.markForCheck();
      });

    this.chargeBasisService.getCharges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(basisList => {
        this.chargeBasisList = basisList;
        this.cdr.markForCheck();
      });
  }

  open(): void {
    this.isOpen = true;
    this.cdr.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    this.showForm = false;
    this.formData = this.getEmptyFormData();
    this.cdr.markForCheck();
  }

  applyFilters(): void {
    let result = this.allCharges;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(c =>
        c.feeName.toLowerCase().includes(term) ||
        (c.description?.toLowerCase().includes(term) ?? false)
      );
    }

    if (this.selectedMode) {
      result = result.filter(c => c.mode === this.selectedMode || c.mode === 'ALL');
    }

    if (this.selectedChargeType) {
      result = result.filter(c => c.chargeType === this.selectedChargeType);
    }

    this.filteredCharges = result;
  }

  getModeDisplay(mode: ChargeMode): string {
    const modeMap: Record<ChargeMode, string> = {
      'SEA_LCL': 'SEA (LCL)',
      'SEA_FCL': 'SEA (FCL)',
      'SEA_ALL': 'SEA (ALL)',
      'AIR': 'AIR',
      'ALL': 'ALL'
    };
    return modeMap[mode] || mode;
  }

  openAddForm(): void {
    this.isEditMode = false;
    this.editingChargeId = null;
    this.formData = this.getEmptyFormData();
    this.showForm = true;
  }

  editCharge(charge: OperationalCharge): void {
    this.isEditMode = true;
    this.editingChargeId = charge.id;
    this.formData = { ...charge };
    this.showForm = true;
  }

  deleteCharge(charge: OperationalCharge): void {
    if (confirm(`Are you sure you want to delete "${charge.feeName}"?`)) {
      this.operationalChargesService.deleteCharge(charge.id);
    }
  }

  saveCharge(): void {
    if (this.isEditMode && this.editingChargeId !== null) {
      this.operationalChargesService.updateCharge(this.editingChargeId, {
        ...this.formData,
        chargeCode: CHARGE_CODE
      });
    } else {
      this.operationalChargesService.addCharge({
        ...this.formData as Omit<OperationalCharge, 'id' | 'createdAt'>,
        chargeCode: CHARGE_CODE,
        isActive: true
      });
    }
    this.closeForm();
  }

  closeForm(): void {
    this.showForm = false;
    this.formData = this.getEmptyFormData();
  }

  private getEmptyFormData(): Partial<OperationalCharge> {
    return {
      feeName: '',
      mode: 'ALL',
      chargeCode: CHARGE_CODE,
      chargeBasis: '',
      defaultPrice: 0,
      currency: 'PHP',
      chargeType: 'Flat Rate',
      isOptional: true,
      portApplication: 'N/A',
      description: '',
      isAgentSpecific: false,
      isTaxable: false,
      isActive: true
    };
  }
}
